import { createSelector } from '@reduxjs/toolkit';

import {
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_SAVED_SEARCHES,
} from '@iconik-io/iconik-api/src/types/object_types';

import {
  COLLECTION_PAGE_ROUTE,
  SAVED_SEARCH_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
} from '../../constants/routes';
import type { AppState } from '../../store/types';
import type { CommonAclsPrefix } from '../common_acls/types';
import { denormalizeSearchItem } from '../search/normalizers';

const routeAclsPrefix: Record<string, CommonAclsPrefix | undefined> = {
  [COLLECTION_PAGE_ROUTE]: OBJECT_TYPE_COLLECTIONS,
  [SHARE_COLLECTION_PAGE_ROUTE]: OBJECT_TYPE_COLLECTIONS,
  [SAVED_SEARCH_PAGE_ROUTE]: OBJECT_TYPE_SAVED_SEARCHES,
};

const getRoute = (state: AppState) => state.routeParams.route;

export const selectRouteAclsPrefix = createSelector(
  getRoute,
  (route) => routeAclsPrefix[route],
);

const routeEntityKey: Record<
  string,
  'collections' | 'saved_search_results' | undefined
> = {
  [COLLECTION_PAGE_ROUTE]: OBJECT_TYPE_COLLECTIONS,
  [SHARE_COLLECTION_PAGE_ROUTE]: 'collections',
  [SAVED_SEARCH_PAGE_ROUTE]: 'saved_search_results',
};

export const getRouteEntityKey = (state: AppState) =>
  routeEntityKey[state.routeParams.route];

const getEntities = (state: AppState) => state.entities;

const getId = (_state: AppState, id: string | undefined) => id;

export const selectCollectionWithFallback = createSelector(
  getEntities,
  getId,
  (entities, id) =>
    id
      ? (entities.collections[id] ?? denormalizeSearchItem(entities, id))
      : undefined,
);

export const selectSavedSearchWithFallback = createSelector(
  getEntities,
  getId,
  (entities, id) =>
    id
      ? (entities.saved_search_results[id] ?? entities.saved_searches[id])
      : undefined,
);

export const selectDirectLinkSegmentId = (
  state: AppState,
): string | undefined => {
  // support legacy 'segment_id' query param for direct linking to a segment
  if (state.routeParams.query.segment_id) {
    return state.routeParams.query.segment_id;
  }
  // For UAD style direct links, extract the first segment ID from segment_ids query param.
  if (state.routeParams.query.segment_ids) {
    try {
      const parsed = JSON.parse(state.routeParams.query.segment_ids);
      if (Array.isArray(parsed) && typeof parsed[0] === 'string') {
        return parsed[0];
      }
    } catch {
      // malformed value — ignore
    }
  }
  return undefined;
};
